import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Instagram, Facebook, Twitter, Globe } from "lucide-react";
import { toast } from "sonner";

const Contact = () => {
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please fill in all fields");
            return;
        }
        setSubmitted(true);
        toast.success("Thank you. Your note has been received.");
        setForm({ name: "", email: "", message: "" });
        setTimeout(() => setSubmitted(false), 4000);
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative py-24 md:py-32 lg:py-40 bg-mint-soft border-t border-ink-900/8"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                <div className="grid grid-cols-12 gap-8">
                    <div className="col-span-12 lg:col-span-4">
                        <div className="section-number">09 / Connect</div>
                        <div className="mt-6 divider" />
                        <div className="mt-4 eyebrow">Office & Press</div>
                    </div>

                    <div className="col-span-12 lg:col-span-8">
                        <motion.h2
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                            className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-ink-900"
                        >
                            Write to <em className="text-bisleri">us</em>.<br />
                            Or simply walk in.
                        </motion.h2>
                    </div>
                </div>

                <div className="mt-16 md:mt-20 grid grid-cols-12 gap-8 lg:gap-16">
                    <motion.form
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                        onSubmit={handleSubmit}
                        className="col-span-12 lg:col-span-7 bg-paper-50 border border-ink-900/10 p-8 md:p-10"
                        data-testid="contact-form"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <label className="block">
                                <span className="eyebrow block mb-3">Name</span>
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    className="w-full bg-transparent border-b border-ink-900/20 focus:border-bisleri outline-none font-display text-xl md:text-2xl text-ink-900 placeholder-ink-500 pb-3 transition-colors"
                                    placeholder="Your name"
                                    data-testid="contact-input-name"
                                />
                            </label>
                            <label className="block">
                                <span className="eyebrow block mb-3">Email</span>
                                <input
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    className="w-full bg-transparent border-b border-ink-900/20 focus:border-bisleri outline-none font-display text-xl md:text-2xl text-ink-900 placeholder-ink-500 pb-3 transition-colors"
                                    placeholder="you@example.com"
                                    data-testid="contact-input-email"
                                />
                            </label>
                        </div>

                        <label className="block mt-10">
                            <span className="eyebrow block mb-3">Message</span>
                            <textarea
                                rows={4}
                                value={form.message}
                                onChange={(e) => setForm({ ...form, message: e.target.value })}
                                className="w-full bg-transparent border-b border-ink-900/20 focus:border-bisleri outline-none font-display text-xl md:text-2xl text-ink-900 placeholder-ink-500 pb-3 resize-none transition-colors"
                                placeholder="Tell us about your enquiry — press, partnership, patient story, or a hello."
                                data-testid="contact-input-message"
                            />
                        </label>

                        <button
                            type="submit"
                            disabled={submitted}
                            data-testid="contact-submit-button"
                            className="mt-10 group inline-flex items-center gap-3 px-8 py-4 bg-bisleri text-white font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-bisleri-deep transition-colors disabled:opacity-60 shadow-lg shadow-bisleri/20"
                        >
                            {submitted ? "Sent" : "Send Message"}
                            <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
                        </button>
                    </motion.form>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                        className="col-span-12 lg:col-span-5 space-y-10"
                    >
                        <div data-testid="contact-phone">
                            <div className="eyebrow mb-3 flex items-center gap-2">
                                <Phone size={11} /> Phone
                            </div>
                            <a href="tel:+919819931418" className="block font-display text-2xl md:text-3xl text-ink-900 hover:text-bisleri transition-colors">
                                +91 98199 31418
                            </a>
                        </div>

                        <div data-testid="contact-email">
                            <div className="eyebrow mb-3 flex items-center gap-2">
                                <Mail size={11} /> Email
                            </div>
                            <a href="mailto:CEO@1rupeeclinic.com" className="block font-display text-2xl md:text-3xl text-ink-900 hover:text-bisleri transition-colors">
                                CEO@1rupeeclinic.com
                            </a>
                            <a href="mailto:account@1rupeeclinic.com" className="block font-display text-xl md:text-2xl text-ink-700 hover:text-bisleri transition-colors mt-1">
                                account@1rupeeclinic.com
                            </a>
                        </div>

                        <div data-testid="contact-website">
                            <div className="eyebrow mb-3 flex items-center gap-2">
                                <Globe size={11} /> Website
                            </div>
                            <a href="https://www.1rupeeclinic.com" target="_blank" rel="noreferrer" className="font-display text-2xl md:text-3xl text-ink-900 hover:text-bisleri transition-colors">
                                www.1rupeeclinic.com
                            </a>
                        </div>

                        <div data-testid="contact-address">
                            <div className="eyebrow mb-3 flex items-center gap-2">
                                <MapPin size={11} /> Registered Office
                            </div>
                            <p className="font-body text-base text-ink-800 leading-relaxed max-w-xs">
                                Basement 2, Diamond Homes Co-Op. Hsg Society,<br />
                                Tanaji Chowk, Opp. Times Of India,<br />
                                Bhaskar Colony, Naupada,<br />
                                Thane West — 400 602, Maharashtra, India.
                            </p>
                        </div>

                        <div data-testid="contact-social">
                            <div className="eyebrow mb-4">Social</div>
                            <div className="flex items-center gap-5">
                                <a href="https://instagram.com/1Rupeeclinics" target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-paper-50 border border-ink-900/10 flex items-center justify-center text-ink-700 hover:text-bisleri hover:border-bisleri transition-colors" aria-label="Instagram" data-testid="social-instagram">
                                    <Instagram size={18} />
                                </a>
                                <a href="https://facebook.com/1Rupeeclinic" target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-paper-50 border border-ink-900/10 flex items-center justify-center text-ink-700 hover:text-bisleri hover:border-bisleri transition-colors" aria-label="Facebook" data-testid="social-facebook">
                                    <Facebook size={18} />
                                </a>
                                <a href="https://twitter.com/1Rupeeclinic" target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-paper-50 border border-ink-900/10 flex items-center justify-center text-ink-700 hover:text-bisleri hover:border-bisleri transition-colors" aria-label="Twitter" data-testid="social-twitter">
                                    <Twitter size={18} />
                                </a>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default Contact;
